<template>
  <div class="purchasers">
    <sikanii-list-page
      title="Purchasers"
      :columns="columnsPurchaser"
      :rows="purchasers"
      :table-loading="tableLoading"
      show-search
      :show-profile-photo="true"
      :total-rows="total"
      remote
      :current-page="currentPage"
      :per-page="perPage"
      search-placeholder="Type search term + Enter"
      @onSearch="handleSearch"
      @onPageChange="handlePageChange"
      @onPerPageChange="handlePerPageChange"
      @onSortChange="handleSortChange"
    >
      <template slot="table-header">
        <agent-filter @filter="(id)=>getCustomers(id)" />
        <div class="col-12">
          <div class="d-flex flex-wrap mb-1">
            <div class="mr-2">
              <strong>Active:
                <b-badge
                  variant="light-primary"
                  style="font-size: 15px;"
                >
                  {{ summary.active }}
                </b-badge>
              </strong>
            </div>
            <div class="mr-2">
              <strong>Inactive:
                <b-badge
                  variant="light-primary"
                  style="font-size: 15px;"
                >
                  {{ summary.inactive }}
                </b-badge>
              </strong>
            </div>
            <div class="mr-2">
              <strong>Males:
                <b-badge
                  variant="light-primary"
                  style="font-size: 15px;"
                >
                  {{ summary.males }}
                </b-badge>
              </strong>
            </div>
            <div class="mr-2">
              <strong>Females:
                <b-badge
                  variant="light-primary"
                  style="font-size: 15px;"
                >
                  {{ summary.females }}
                </b-badge>
              </strong>
            </div>
          </div>
        </div>
      </template>
      <template slot="right-extra-header-actions">
        <div class="d-flex align-self-center align-items-start">
          <sikanii-button
            v-responsive="$store.getters['app/largeScreen']"
            name="manage_occupations"
            icon="SettingsIcon"
            label="Occupations"
            tool-tip-text="Manage Occupations (CRUD)"
            variant="outline-primary"
            class="mr-1"
            @click="showOccupationModal()"
          />
          <sikanii-button
            v-responsive="$store.getters['app/largeScreen']"
            name="manage_branches"
            icon="SettingsIcon"
            label="Branches"
            tool-tip-text="Manage Branches (CRUD)"
            variant="outline-primary"
            class="mr-1"
            @click="showBranchModal()"
          />
          <sikanii-button
            v-responsive="$store.getters['app/largeScreen']"
            name="manage_purchasers"
            icon="SettingsIcon"
            label="Purchasers"
            tool-tip-text="Manage Purchasers (CRUD)"
            @click="showPurchaserModal()"
          />
          <sikanii-dropdown
            v-responsive="$store.getters['app/smallScreen']"
            left
          >
            <sikanii-dropdown-item
              icon="SettingsIcon"
              text="Manage Purchasers"
              @click="showPurchaserModal()"
            />
            <sikanii-dropdown-item
              icon="SettingsIcon"
              text="Manage Branches"
              @click="showBranchModal()"
            />
            <sikanii-dropdown-item
              icon="SettingsIcon"
              text="Manage Occupations"
              @click="showOccupationModal()"
            />
          </sikanii-dropdown>
        </div>
      </template>
      <template #status="{row}">
        <b-badge :variant="row.item.status==='active'?'primary':'danger'">
          {{ row.item.status.toUpperCase() }}
        </b-badge>
      </template>
      <template #first_name="{row}">
        {{ row.item.first_name }} {{ row.item.last_name }}
      </template>
      <template #balance="{row}">
        <b-badge variant="light-danger">
          {{ $store.getters['auth/currency'] }}{{ row.item.balance | currency }}
        </b-badge>
      </template>
      <template #action="{row}">
        <sikanii-button
          icon="Edit2Icon"
          :icon-only="true"
          variant="flat-primary"
          tool-tip-text="Edit Purchaser Information"
          @click="editPurchaser(row.item.id)"
        />
      </template>
    </sikanii-list-page>
    <branch-setup
      :modal-opened="branchModalOpened"
      @modalClosed="closeModal('branch')"
    />
    <customer-setup
      :modal-opened="purchaserModalOpened"
      :edit-id="editPurchaserId"
      :purchaser-mode="true"
      @modalClosed="closeModal('purchaser')"
    />
    <occupation-setup
      :modal-opened="occupationModalOpened"
      :edit-id="editOccupationId"
      @modalClosed="closeModal('occupation')"
    />
  </div>
</template>

<script>
import SikaniiButton from '@/layouts/components/SikaniiButton.vue'
import showToast from '@/libs/mixins/showToast'
import SikaniiListPage from '@/layouts/components/SikaniiListPage.vue'
import confirm from '@/libs/mixins/confirm'
import SikaniiDropdown from '@/layouts/components/SikaniiDropdown.vue'
import SikaniiDropdownItem from '@/layouts/components/SikaniiDropdownItem.vue'
import { BBadge } from 'bootstrap-vue'
import CustomerSetup from '@/views/susu-savings/customers/CustomerSetup.vue'
import OccupationSetup from '@/views/susu-savings/customers/OccupationSetup.vue'
import BranchSetup from '@/views/susu-savings/customers/BranchSetup'
import AgentFilter from '@/layouts/components/AgentFilter'

export default {
  name: 'PurchasersIndex',
  components: {
    AgentFilter,
    BranchSetup,
    OccupationSetup,
    CustomerSetup,
    SikaniiDropdownItem,
    SikaniiDropdown,
    SikaniiListPage,
    SikaniiButton,
    BBadge,
  },
  mixins: [showToast, confirm],
  data() {
    return {
      editPurchaserId: null,
      purchaserModalOpened: false,
      editOccupationId: null,
      occupationModalOpened: false,
      tableLoading: false,
      currentPage: 1,
      perPage: 10,
      searchTerm: '',
      initializing: false,
      branchModalOpened: false,
      sort: {
        column: 'created_at',
        order: 'desc',
      },
    }
  },
  computed: {
    purchasers() {
      return this.$store.getters['susuSavings/customers']
    },
    summary() {
      return this.$store.getters['susuSavings/summary']
    },
    total() {
      return this.$store.getters['susuSavings/totalCustomers']
    },
    columnsPurchaser() {
      const show = this.$store.getters['app/isLargeScreen']
      return [
        {
          key: 'index',
          label: '#',
        },
        {
          key: 'photo',
          label: 'Photo',
        },
        {
          key: 'first_name',
          label: 'Full Name',
          sortable: show,
        },
        {
          key: 'reference',
          label: 'Reference',
        },
        {
          key: 'status',
          label: 'Status',
        },
        {
          key: 'phone',
          label: 'Phone',
        },
        {
          key: 'balance',
          label: 'Balance',
        },
        {
          key: 'action',
          label: 'Action',
        },
      ]
    },
  },
  mounted() {
    this.initializing = true
    this.getCustomers()
    this.getOccupationsBranchesAndSettings()
  },
  methods: {
    editPurchaser(id) {
      this.editPurchaserId = id
      this.showPurchaserModal()
    },
    closeModal(type) {
      // eslint-disable-next-line default-case
      switch (type) {
        case 'purchaser':
          this.purchaserModalOpened = false
          this.editPurchaserId = null
          break
        case 'occupation':
          this.occupationModalOpened = false
          this.editOccupationId = null
          break
        case 'branch':
          this.branchModalOpened = false
          break
      }
    },
    showPurchaserModal() {
      this.purchaserModalOpened = true
    },
    showOccupationModal() {
      this.occupationModalOpened = true
    },
    showBranchModal() {
      this.branchModalOpened = true
    },
    handleSearch(search) {
      this.searchTerm = search
      this.getCustomers()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.getCustomers()
    },
    handlePerPageChange(perPage) {
      if (!this.initializing) {
        this.perPage = perPage
        this.getCustomers()
      }
    },
    handleSortChange(sort) {
      this.sort.column = sort.sortBy
      this.sort.order = sort.sortDesc ? 'desc' : 'asc'
      this.getCustomers()
    },
    getOccupationsBranchesAndSettings() {
      this.$http.get('susu-savings/branches-occupations-and-settings')
        .then(res => {
          this.$store.commit('susuSavings/setOccupations', res.data.occupations)
          this.$store.commit('susuSavings/setBranches', res.data.branches)
        })
    },
    getCustomers(agentId = null) {
      this.tableLoading = true
      this.$http.get(`susu-savings/customers?agent_id=${agentId || ''}&is_purchaser=true&per_page=${this.perPage}&page=${this.currentPage}&search=${this.searchTerm}&sort_col=${this.sort.column}&sort_order=${this.sort.order}`)
        .then(res => {
          this.$store.commit('susuSavings/setCustomers', res.data)
        }).finally(() => {
          this.initializing = false
          this.tableLoading = false
        })
    },
  },
}
</script>
